<template>
  <div>
    <v-stepper v-model="currentStep" class="form-stepper">
      <v-stepper-header id="stepper-header">
        <template v-for="(step, index) in formSteps">
          <v-stepper-step
            :key="step.value"
            :complete="currentStep > step.value"
            :step="step.value"
            :class="{'clickable': step.value < currentStep && !loading}"
            @click="skipToStep(step.value)"
          >
            {{ step.title }}
          </v-stepper-step>

          <v-divider v-if="index < formSteps.length - 1" :key="step.value + 'divider'"/>
        </template>
      </v-stepper-header>

      <v-stepper-items class="stepper-items">
        <v-stepper-content step="1">
          <rca-calculator
            @updateAnswers="updateFirstStepAnswers"
            @formStarted="scrollToTop"
            @nextStep="nextStep"
            @trackEvent="eventTracking"
          />
        </v-stepper-content>

        <v-stepper-content step="2">
          <step-with-sidebar :order-total="orderTotal">
            <template #step-content>
              <vehicle-step
                :vehicle-type="vehicleType"
                section-type="rca"
                @updateAnswers="updateSecondStepAnswers"
                @nextStep="nextStep"
              />
            </template>
          </step-with-sidebar>
        </v-stepper-content>

        <v-stepper-content step="3">
          <step-with-sidebar :order-total="orderTotal">
            <template #step-content>
              <driver-step
                :driver-entity="driverEntity"
                :drivers-number="driversNumber"
                section-type="rca"
                @updateAnswers="updateThirdStepAnswers"
                @nextStep="nextStep"
              />
            </template>
          </step-with-sidebar>
        </v-stepper-content>
        <v-stepper-content step="4">
          <step-with-sidebar :order-total="orderTotal">
            <template #step-content>
              <confirmation-step
                section-type="rca"
                @updateStartDate="updateStartDate"
                @updateContactInfo="updateContactInfo"
                @updateShippingInfo="updateShippingInfo"
                @nextStep="nextStep"
              />
            </template>
          </step-with-sidebar>
        </v-stepper-content>
        <v-overlay :value="loading" absolute>
          <v-progress-circular
            indeterminate
            size="64"
          />
        </v-overlay>
      </v-stepper-items>
    </v-stepper>
  </div>
</template>

<script>
import { getSectionSteps } from '@/_utils/static-data';
import RcaCalculator from '@/components/rca/RcaCalculator';
import VehicleStep from '@/components/rca/VehicleStep';
import DriverStep from '@/components/rca/DriverStep';
import ConfirmationStep from '@/components/rca/ConfirmationStep';
import StepWithSidebar from '@/components/rca/StepWithSidebar';
import { dataLayerPost, postToTelegram } from '@/_utils/helpers';

export default {
  name: 'RcaSection',
  components: {
    StepWithSidebar,
    ConfirmationStep,
    DriverStep,
    VehicleStep,
    RcaCalculator
  },
  data() {
    return {
      currentStep: 1,
      orderTotal: 0,
      validRca: false,
      validVehicle: false,
      validDriver: false,
      validContact: false,
      validStartData: false,
      validShipping: false,
      rcaAnswers: [],
      vehicleAnswers: [],
      vehicleData: [],
      legalEntity: [],
      physicalEntity: [],
      additionalDrivers: [],
      contactData: [],
      startDate: null,
      shippingInfo: [],
      loading: false
    };
  },
  computed: {
    formSteps() {
      return getSectionSteps('rca');
    },
    vehicleType() {
      const vehicleTypeAnswer = this.rcaAnswers.find(el => el.questionId === 4);
      return vehicleTypeAnswer || null;
    },
    driverEntity() {
      const entityAnswer = this.rcaAnswers.find(el => el.questionId === 2);
      if (entityAnswer?.id === 1) {
        return 'physical';
      } else if (entityAnswer?.id === 2) {
        return 'legal';
      }
      return null;
    },
    driversNumber() {
      const numbersAnswer = this.rcaAnswers.find(el => el.questionId === 6);
      if (numbersAnswer?.id === 1) {
        return 'limited';
      } else if (numbersAnswer?.id === 2) {
        return 'unlimited';
      }
      return null;
    }
  },
  methods: {
    getSimpleSectionAnswers() {
      let answerIndex = 1;
      const allAnswers = {};
      allAnswers[`${answerIndex}_valibilitatea din data`] = this.startDate;
      for (const rcaAnswer of this.rcaAnswers) {
        if (rcaAnswer) {
          answerIndex++;
          allAnswers[`${answerIndex}_${rcaAnswer.questionText}`] = rcaAnswer.text || rcaAnswer.ids;
        }
      }
      for (const vehicleAnswer of this.vehicleAnswers) {
        if (vehicleAnswer) {
          answerIndex++;
          allAnswers[`${answerIndex}_${vehicleAnswer.questionText}`] = vehicleAnswer.text;
        }
      }
      for (const vehicleDataAnswer of this.vehicleData) {
        if (vehicleDataAnswer) {
          answerIndex++;
          allAnswers[`${answerIndex}_${vehicleDataAnswer.questionText}`] = vehicleDataAnswer.text;
        }
      }

      if (this.legalEntity?.length > 0) {
        for (const legalAnswer of this.legalEntity) {
          if (legalAnswer) {
            answerIndex++;
            allAnswers[`${answerIndex}_${legalAnswer.questionText}`] = legalAnswer.text;
          }
        }
      } else if (this.physicalEntity?.length > 0) {
        for (const physicalAnswer of this.physicalEntity) {
          if (physicalAnswer) {
            answerIndex++;
            allAnswers[`${answerIndex}_${physicalAnswer.questionText}`] = physicalAnswer.text;
          }
        }
        if (this.additionalDrivers?.length > 0) {
          answerIndex++;
          allAnswers[`${answerIndex}_alti conducatori`] = [];
          for (const additionalDriver of this.additionalDrivers) {
            if (additionalDriver?.info?.length  > 0) {
              allAnswers[`${answerIndex}_alti conducatori`].push(additionalDriver.info
                .map(infoEl => `${infoEl.question} ${infoEl.answer}`));
            }
          }
        }
      }
      for (const contactAnswer of this.contactData) {
        if (contactAnswer) {
          answerIndex++;
          allAnswers[`${answerIndex}_Contact-${contactAnswer.questionText}`] = contactAnswer.text;
        }
      }
      for (const shippingAnswer of this.shippingInfo) {
        if (shippingAnswer) {
          answerIndex++;
          allAnswers[`${answerIndex}_${shippingAnswer.questionText}`] = shippingAnswer.text;
        }
      }
      return allAnswers;
    },
    getMetaAnswers() {
      let answerIndex = 1;
      let allAnswers = [
        {
          key: `${answerIndex}_Valabilitatea din data:`,
          value: this.startDate
        }
      ];
      allAnswers = [...allAnswers, ...this.rcaAnswers.map((el) => {
        if (el) {
          answerIndex++;
          return {
            key: `${answerIndex}_${el.questionText}`,
            value: el.text || JSON.stringify(el.ids)
          };
        }
        return null;
      })];
      const simpleVehicleAnswers = this.vehicleAnswers.map((el) => {
        if (el) {
          answerIndex++;
          return {
            key: `${answerIndex}_${el.questionText}`,
            value: el.text
          };
        }
        return null;
      });
      const vehicleDataAnswers = this.vehicleData.map((el) => {
        if (el) {
          answerIndex++;
          return {
            key: `${answerIndex}_${el.questionText}`,
            value: el.text
          };
        }
        return null;
      });
      allAnswers = [...allAnswers, ...simpleVehicleAnswers, ...vehicleDataAnswers];
      if (this.legalEntity?.length > 0) {
        allAnswers = [...allAnswers, ...this.legalEntity.map((el) => {
          if (el) {
            answerIndex++;
            return {
              key: `${answerIndex}_${el.questionText}`,
              value: el.text
            };
          }
          return null;
        })];
      } else if (this.physicalEntity?.length > 0) {
        allAnswers = [...allAnswers, ...this.physicalEntity.map((el) => {
          if (el) {
            answerIndex++;
            return {
              key: `${answerIndex}_${el.questionText}`,
              value: el.text
            };
          }
          return null;
        })];
        if (this.additionalDrivers?.length > 0) {
          answerIndex++;
          allAnswers.push({
            key: `${answerIndex}_Alti conducatori`,
            value: JSON.stringify(this.additionalDrivers.map((el) => {
              if (el?.info?.length > 0) {
                return el.info.map(infoEl => `${infoEl.question} ${infoEl.answer}`);
              }
              return null;
            }))
          });
        }
      }
      allAnswers = [...allAnswers, ...this.contactData.map((el) => {
        if (el) {
          answerIndex++;
          return {
            key: `${answerIndex}_Contact-${el.questionText}`,
            value: el.text
          };
        }
        return null;
      })];
      allAnswers = [...allAnswers, ...this.shippingInfo.map((el) => {
        if (el) {
          answerIndex++;
          return {
            key: `${answerIndex}_${el.questionText}`,
            value: el.text
          };
        }
        return null;
      })];
      return allAnswers;
    },
    async createOrder({ price, answers }) {
      try {
        const url = 'https://qasig.md/wp-json/wp-calculator/v1/create-order';
        const newOrder = {
          status: 'pending',
          currency: 'MDL',
          billing: this.getBillingInfo(),
          shipping: this.getShippingInfo(),
          line_items: [
            {
              product_id: 6261,
              total: price,
              quantity: 1
            }
          ],
          meta_data: answers
        };
        const res = await fetch(url, {
          method: 'POST',
          body: JSON.stringify(newOrder)
        });
        return res.json();
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    scrollToTop() {
      setTimeout(() => {
        this.$scrollTo(
          '#stepper-header',
          400,
          {
            // offset: -72
          });
      }, 500);
    },
    updateFirstStepAnswers({ isValid, total, answers }) {
      this.validRca = isValid;
      this.orderTotal = total;
      this.rcaAnswers = answers;
    },
    updateSecondStepAnswers({ isValid, vehicleData, answers }) {
      this.validVehicle = isValid;
      this.vehicleAnswers = answers;
      this.vehicleData = vehicleData;
    },
    updateThirdStepAnswers({ isValid, legalEntity, physicalEntity, additionalDrivers }) {
      this.validDriver = isValid;
      this.legalEntity = legalEntity;
      this.physicalEntity = physicalEntity;
      this.additionalDrivers = additionalDrivers;
    },
    updateStartDate({ startDate, isValid }) {
      this.validStartData = isValid;
      this.startDate = startDate;
    },
    updateContactInfo({ data, isValid }) {
      this.validContact = isValid;
      this.contactData = data;
    },
    updateShippingInfo({ data, isValid }) {
      this.validShipping = isValid;
      this.shippingInfo = data;
    },
    getBillingInfo() {
      if (this.contactData.length > 0) {
        return {
          first_name: this.contactData.find(el => el.value === 'name')?.text,
          last_name: this.contactData.find(el => el.value === 'surname')?.text,
          address_1: '',
          address_2: '',
          city: '',
          state: '',
          postcode: '',
          country: 'MD',
          email: this.contactData.find(el => el.value === 'email')?.text,
          phone: this.contactData.find(el => el.value === 'phone')?.text
        };
      }
      return false;
    },
    getShippingInfo() {
      if (this.shippingInfo.length > 1) {
        return {
          first_name: this.contactData.find(el => el.value === 'name')?.text,
          last_name: this.contactData.find(el => el.value === 'surname')?.text,
          company: 'NOTES: ' + this.shippingInfo.find(el => el.value === 'notes')?.text + ' '
            + this.shippingInfo.find(el => el.value === 'district')?.text,
          address_1: this.shippingInfo.find(el => el.value === 'address')?.text,
          address_2: 'DATA LIVRARII: ' + this.shippingInfo.find(el => el.value === 'shippingDate')?.text,
          city: 'Chisinau',
          state: '',
          postcode: '',
          country: 'MD'
        };
      }
      return {};
    },
    async complete() {
      this.loading = true;
      const metaAnswers = this.getMetaAnswers();
      const res = await this.createOrder({
        price: this.orderTotal,
        answers: metaAnswers
      });
      const billingInfo = this.getBillingInfo();
      const firstName = billingInfo.first_name;
      const lastName = billingInfo.last_name;
      const phone = billingInfo.phone;
      await postToTelegram(`COMANDA NOUA%0AId: ${res.id};%0ATip: RCA;%0APret: ${this.orderTotal} MDL;%0ANume: ${firstName} ${lastName};%0ATelefon: ${phone};`);
      if (res?.id && res?.order_key) {
        const checkoutApi = `https://qasig.md/checkout/order-pay/${res.id}/?pay_for_order=true&key=${res.order_key}`;
        window.location.replace(checkoutApi);
      } else {
        this.$toasted.error('Ceva n-a mers bine! Vă rugăm să verificați datele introduse');
      }
      this.loading = false;
    },
    nextStep(eventName) {
      if (this.loading) { return; }
      if (this.currentStep < this.formSteps.length) {
        this.currentStep++;
      } else if (this.currentStep === this.formSteps.length) {
        this.complete();
      }
      this.scrollToTop();
      this.eventTracking({ eventName });
    },
    eventTracking({ eventName, payload = {} }) {
      try {
        const metaAnswers = this.getMetaAnswers();
        setTimeout(() => {
          dataLayerPost(eventName, {
            answers: metaAnswers,
            price: this.orderTotal,
            ...payload
          });
        }, 500);
      } catch (e) {
        console.log(e);
      }
    },
    prevStep() {
      if (this.currentStep > 1) {
        this.currentStep--;
      }
      this.scrollToTop();
    },
    skipToStep(step) {
      if (step < this.currentStep && !this.loading) {
        this.currentStep = step;
      }
    }
  },
};
</script>

<style scoped>
.clickable {
  cursor: pointer;
}
.stepper-items {
  position: relative;
}
</style>
